import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { AxiosResponse } from "axios";
import { usersApi, sessionsApi, tutorsApi } from "../../services/api";
import { FileText, Star } from "lucide-react";

interface CourseStat {
  id: string;
  course: string;
  faculty: string;
  completion: number;
  averageScore: number;
  tutorHours: number;
  activeStudents: number;
}

interface TutorStat {
  tutor_id: number;
  name: string;
  total_sessions: number;
  rating: number;
  total_hours: number;
}

interface StudentStat {
  student_id: number;
  name: string;
  attendance_rate: number;
  sessions_attended: number;
}

const Reports: React.FC = () => {
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState("quarter");
  const [faculty, setFaculty] = useState("all");
  const [courseStats, setCourseStats] = useState<CourseStat[]>([]);
  const [topTutors, setTopTutors] = useState<TutorStat[]>([]);
  const [topStudents, setTopStudents] = useState<StudentStat[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total_sessions: 0,
    active_students: 0,
    average_rating: 0,
    total_hours: 0,
    total_tutors: 0,
    completed_sessions: 0
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch coordinator stats for KPIs
        const statsResponse = await usersApi.getCoordinatorStats() as AxiosResponse<any>;
        setStats({
          total_sessions: statsResponse.data?.total_sessions || 0,
          active_students: statsResponse.data?.active_students || 0,
          average_rating: statsResponse.data?.average_rating || 0,
          total_hours: statsResponse.data?.total_hours || 0,
          total_tutors: statsResponse.data?.total_tutors || 0,
          completed_sessions: statsResponse.data?.completed_sessions || 0
        });

        // Fetch sessions for course breakdown
        const sessionsResponse = await sessionsApi.getSessions({}) as AxiosResponse<any>;
        const sessions = sessionsResponse.data || [];

        // Group by subject and calculate stats
        const courseMap = new Map();
        sessions.forEach((session: any) => {
          const key = session.subject_id;
          if (!courseMap.has(key)) {
            courseMap.set(key, {
              id: key,
              course: session.subject_name || session.subject_code,
              faculty: session.department || 'N/A',
              completion: 0,
              totalSessions: 0,
              completedSessions: 0,
              averageScore: 0,
              tutorHours: 0,
              activeStudents: 0
            });
          }
          const course = courseMap.get(key);
          course.totalSessions++;
          if (session.status === 'completed') {
            course.completedSessions++;
          }
          course.tutorHours += 2; // Estimate 2h per session
          course.activeStudents += session.student_count || 0;
        });

        const courseStatsData = Array.from(courseMap.values()).map((course: any) => ({
          ...course,
          completion: course.totalSessions > 0 ? Math.round((course.completedSessions / course.totalSessions) * 100) : 0,
          averageScore: 4.5 // Placeholder
        }));

        setCourseStats(courseStatsData);
        
        // Fetch top tutors
        const tutorsResponse = await tutorsApi.getTutors() as AxiosResponse<any>;
        const tutors = tutorsResponse.data || [];
        const tutorsWithStats = tutors
          .map((t: any) => ({
            tutor_id: t.tutor_id,
            name: t.full_name || t.name,
            total_sessions: t.total_sessions || 0,
            rating: t.rating || 0,
            total_hours: (t.total_sessions || 0) * 2
          }))
          .sort((a: TutorStat, b: TutorStat) => b.rating - a.rating)
          .slice(0, 10);
        setTopTutors(tutorsWithStats);
        
      } catch (error: any) {
        console.error("Error fetching reports:", error);
        toast.error("Không thể tải báo cáo");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredCourses = useMemo(() => {
    if (faculty === "all") return courseStats;
    return courseStats.filter((course) => course.faculty === faculty);
  }, [faculty, courseStats]);

  const kpiCards = [
    {
      label: "Phiên học hoàn thành",
      value: loading ? "..." : stats.completed_sessions.toString(),
      trend: `${stats.total_sessions} tổng số`,
      trendColor: "text-gray-600",
    },
    {
      label: "Sinh viên hoạt động",
      value: loading ? "..." : stats.active_students.toString(),
      trend: `Tổng ${stats.total_tutors} tutors`,
      trendColor: "text-blue-600",
    },
    {
      label: "Điểm hài lòng trung bình",
      value: loading ? "..." : `${stats.average_rating.toFixed(1)}/5`,
      trend: stats.average_rating >= 4.0 ? "Tốt" : "Cần cải thiện",
      trendColor: stats.average_rating >= 4.0 ? "text-green-600" : "text-yellow-600",
    },
    {
      label: "Giờ tutor đóng góp",
      value: loading ? "..." : `${stats.total_hours}h`,
      trend: `~${Math.round(stats.total_hours / Math.max(stats.total_tutors, 1))}h/tutor`,
      trendColor: "text-purple-600",
    },
  ];

  const facultyOptions = [
    { value: "all", label: "Tất cả khoa" },
    { value: "CS", label: "Khoa Khoa học & Kỹ thuật Máy tính" },
    { value: "EE", label: "Khoa Điện - Điện tử" },
    { value: "BS", label: "Khoa Cơ khí" },
  ];

  const recentReports = [
    {
      name: "Tổng quan hoạt động tháng 11/2025",
      createdAt: "2025-11-15",
      author: "Nguyễn Thị Hằng",
      status: "Hoàn thành",
    },
    {
      name: "Tiến độ sinh viên khoa CSE",
      createdAt: "2025-11-10",
      author: "Phạm Quốc Bảo",
      status: "Đang soạn",
    },
    {
      name: "Đánh giá chất lượng tutor",
      createdAt: "2025-11-02",
      author: "Lê Mỹ Anh",
      status: "Hoàn thành",
    },
  ];

  const formatDate = (value: string) =>
    new Date(value).toLocaleDateString("vi-VN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

  return (
    <div className="container mx-auto px-4 py-8 space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-sm uppercase tracking-wide text-blue-600 font-semibold">
            Báo cáo & Phân tích
          </p>
          <h1 className="text-3xl font-bold text-gray-900">Bảng điều khiển báo cáo</h1>
          <p className="text-gray-500">
            Theo dõi hiệu suất học tập, hoạt động tutor và mức độ tham gia của sinh viên
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="month">Tháng này</option>
            <option value="quarter">Quý hiện tại</option>
            <option value="year">Năm học</option>
          </select>
          <button
            onClick={() => navigate("/export-report")}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition font-semibold inline-flex items-center gap-2"
          >
            <FileText className="w-5 h-5" />
            Xuất báo cáo
          </button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpiCards.map((card) => (
          <div key={card.label} className="bg-white rounded-lg shadow-md p-5">
            <p className="text-sm text-gray-500">{card.label}</p>
            <p className="text-3xl font-bold text-gray-900 my-2">{card.value}</p>
            <p className={`text-sm font-semibold ${card.trendColor}`}>{card.trend}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Tổng quan khoá học</h2>
            <p className="text-gray-500 text-sm">
              Dữ liệu đã chuẩn hóa theo mốc thời gian: {timeRange === "month" ? "Tháng hiện tại" : timeRange === "quarter" ? "Quý hiện tại" : "Năm học"}
            </p>
          </div>
          <select
            value={faculty}
            onChange={(e) => setFaculty(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {facultyOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="mt-6 overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead>
              <tr className="text-gray-500 border-b">
                <th className="py-3 pr-4">Môn học</th>
                <th className="py-3 pr-4">Hoàn thành</th>
                <th className="py-3 pr-4">Điểm TB</th>
                <th className="py-3 pr-4">Giờ tutor</th>
                <th className="py-3">SV hoạt động</th>
              </tr>
            </thead>
            <tbody>
              {filteredCourses.map((course) => (
                <tr key={course.id} className="border-b last:border-0">
                  <td className="py-4 pr-4">
                    <p className="font-semibold text-gray-900">{course.course}</p>
                    <p className="text-xs text-gray-500">
                      {facultyOptions.find((opt) => opt.value === course.faculty)?.label}
                    </p>
                  </td>
                  <td className="py-4 pr-4">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-gray-100 rounded-full">
                        <div
                          className="h-full rounded-full bg-blue-500"
                          style={{ width: `${course.completion}%` }}
                        />
                      </div>
                      <span className="font-semibold text-gray-900">
                        {course.completion}%
                      </span>
                    </div>
                  </td>
                  <td className="py-4 pr-4 font-semibold text-gray-900">
                    {course.averageScore.toFixed(1)} / 10
                  </td>
                  <td className="py-4 pr-4 text-gray-700">{course.tutorHours}h</td>
                  <td className="py-4 text-gray-700">{course.activeStudents} SV</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top Tutors Section */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Top Tutors</h2>
        <p className="text-gray-500 text-sm mb-6">Xếp hạng theo điểm đánh giá trung bình</p>
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead>
              <tr className="text-gray-500 border-b">
                <th className="py-3 pr-4">#</th>
                <th className="py-3 pr-4">Tên Tutor</th>
                <th className="py-3 pr-4">Số phiên</th>
                <th className="py-3 pr-4">Đánh giá</th>
                <th className="py-3">Tổng giờ</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="py-4 text-center text-gray-500">Đang tải...</td></tr>
              ) : topTutors.length === 0 ? (
                <tr><td colSpan={5} className="py-4 text-center text-gray-500">Chưa có dữ liệu</td></tr>
              ) : (
                topTutors.map((tutor, index) => (
                  <tr key={tutor.tutor_id} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="py-4 pr-4 font-semibold text-gray-700">{index + 1}</td>
                    <td className="py-4 pr-4 font-semibold text-gray-900">{tutor.name}</td>
                    <td className="py-4 pr-4 text-gray-700">{tutor.total_sessions} phiên</td>
                    <td className="py-4 pr-4">
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-semibold bg-yellow-100 text-yellow-800">
                        <Star className="w-4 h-4 fill-yellow-600" /> {tutor.rating.toFixed(1)}
                      </span>
                    </td>
                    <td className="py-4 text-gray-700">{tutor.total_hours}h</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent reports and Summary */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Tổng quan hệ thống</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between pb-3 border-b">
              <span className="text-gray-600">Tổng số khóa học</span>
              <span className="font-bold text-gray-900">{courseStats.length}</span>
            </div>
            <div className="flex items-center justify-between pb-3 border-b">
              <span className="text-gray-600">Tutors đang hoạt động</span>
              <span className="font-bold text-gray-900">{stats.total_tutors}</span>
            </div>
            <div className="flex items-center justify-between pb-3 border-b">
              <span className="text-gray-600">Sinh viên tham gia</span>
              <span className="font-bold text-gray-900">{stats.active_students}</span>
            </div>
            <div className="flex items-center justify-between pb-3 border-b">
              <span className="text-gray-600">Tổng phiên học</span>
              <span className="font-bold text-gray-900">{stats.total_sessions}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Điểm đánh giá TB</span>
              <span className="inline-flex items-center gap-1 font-bold text-green-600"><Star className="w-5 h-5 fill-green-600" /> {stats.average_rating.toFixed(1)}/5.0</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-900">Báo cáo gần đây</h3>
            <button className="text-blue-600 text-sm font-semibold hover:text-blue-700" onClick={() => navigate("/export-report")}>Xem tất cả</button>
          </div>
          <div className="space-y-3">
            {recentReports.map((report) => (
              <div
                key={report.name}
                className="p-4 border border-gray-100 rounded-lg hover:bg-gray-50 transition"
              >
                <div className="flex items-center justify-between mb-1">
                  <p className="font-semibold text-gray-900">{report.name}</p>
                  <span
                    className={`text-xs font-semibold px-2 py-1 rounded-full ${report.status === "Hoàn thành"
                        ? "bg-green-100 text-green-700"
                        : "bg-yellow-100 text-yellow-700"
                      }`}
                  >
                    {report.status}
                  </span>
                </div>
                <p className="text-sm text-gray-500">
                  Người lập: {report.author} • {formatDate(report.createdAt)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports;